import React, { useState, useEffect } from 'react';
import { supabase, ensureValidSession } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';
import { Users, Sparkles, Coins, Loader2, AlertCircle, Key, FileText, Gem, Star, Palette, Gift, MapPin, Coffee, Sun, Moon, Zap, Award } from 'lucide-react';
import AnalysisModal from '../components/AnalysisModal';
import CreditPurchaseModal from '../components/CreditPurchaseModal';
import SajuGrid from '../components/saju/SajuGrid';
import { getShiShenStyle, translateShiShen, formatHiddenStems } from '../constants/saju';
import { experimental_useObject as useObject } from '@ai-sdk/react';
import { analysisSchema } from '../config/schemas';
import { calculateSaju } from '../utils/sajuUtils';

interface Profile {
  id: string;
  name: string;
  email: string | undefined;
  gender: string;
  mbti: string;
  birth_date: string;
  birth_time: string | null;
}

interface Analysis {
  keywords: string;
  reportTitle?: string;
  fusionNickname?: string;
  nature?: {
    title: string;
    dayPillarSummary: string;
    dayMasterAnalysis: string;
    dayBranchAnalysis: string;
    monthBranchAnalysis: string;
  };
  fiveElements?: {
    title: string;
    elements: Array<{ element: string; count: number; function: string; interpretation: string }>;
    summary: string;
  };
  persona?: {
    title: string;
    mbtiNickname: string;
    dominantFunction: string;
    auxiliaryFunction: string;
    tertiaryFunction: string;
    inferiorFunction: string;
  };
  deepIntegration?: {
    sajuBaseAnalysis?: string;
    mbtiIntegration?: string;
    synergyPoints?: Array<{ subtitle: string; content: string }>;
    title?: string;
    integrationPoints?: Array<{ subtitle: string; content: string }>;
  };
  lifeGuideline?: {
    lightAndShadow: {
      light: string;
      shadow: string;
      solution: string;
    };
    luckyBooster: {
      luckyColor: string;
      luckyItem: string;
      luckyPlace: string;
      dailyRoutine: string;
    };
  };
  yearlyFortune?: any;
  monthlyFortune?: any;
  warnings?: any;
  fieldStrategies?: any;
  finalSolution?: any;
  saju?: any;
  // Legacy fields
  typeDescription?: string;
  elementAnalysis?: string;
  commonalities?: string;
}


interface MyPageProps {
  onOpenDeepReport: (reportType?: string) => void;
  onOpenMbtiSaju: () => void;
  onJamidusuClick: () => void;
  onOpenCompatibility: () => void;
  credits: number;
  refreshCredits: (force?: boolean, session?: any) => Promise<void>;
  purchaseCredits: (planId: string, pricePaid: number, credits: number, paymentId?: string) => Promise<boolean>;
  spendCredits: (serviceType: any) => Promise<boolean>;
  checkSufficientCredits: (serviceType: any) => boolean;
  getCost: (serviceType: any) => number;
  session: any;
}



const MyPage: React.FC<MyPageProps> = ({ 
  onOpenDeepReport,
  onOpenMbtiSaju, 
  onJamidusuClick, 
  onOpenCompatibility, 
  credits, 
  refreshCredits, 
  purchaseCredits,
  spendCredits,
  checkSufficientCredits,
  getCost,
  session: initialSession 
}) => {
  const [loading, setLoading] = useState(true);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const navigate = useNavigate();
  const [isCreditModalOpen, setIsCreditModalOpen] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  // Core Streaming Hook
  const { object: coreObj, submit: submitCore } = useObject({
    api: '/api/analyze?part=core',
    schema: analysisSchema,
    headers: async () => {
      const activeSession = await ensureValidSession();
      return { 'Authorization': `Bearer ${activeSession?.access_token || initialSession?.access_token || ''}` };
    },
    onFinish: ({ object, error: validationError }) => {
      if (object) {
        const activeBirthDate = profile?.birth_date;
        const activeBirthTime = profile?.birth_time;
        const sajuData = activeBirthDate ? calculateSaju(activeBirthDate, activeBirthTime ?? null) : null;
        const finalData = { ...object, saju: sajuData };
        
        setAnalysis(finalData as any);
        
        try {
          localStorage.setItem('mbti_saju_analysis', JSON.stringify(finalData));
        } catch (e) {
          console.error('localStorage backup save error:', e);
        }

        supabase.auth.updateUser({
          data: { analysis: finalData }
        }).then(() => {
          if (refreshCredits) refreshCredits();
        }).catch((err) => {
          console.error('Supabase metadata update error:', err);
        });
      } else {
        console.error('Core Analysis validation failed:', validationError);
        setError('분석 결과를 생성하지 못했습니다. 다시 시도해 주세요.');
      }
      setAnalysisLoading(false);
    },
    onError: (err) => {
      console.error('Core Analysis Error:', err);
      setError(err.message || '분석 중 오류가 발생했습니다.');
      setAnalysisLoading(false);
    }
  });

  // Sync partial results
  useEffect(() => {
    if (coreObj) {
      const sajuData = profile?.birth_date ? calculateSaju(profile.birth_date, profile.birth_time ?? null) : null;
      setAnalysis((prev: any) => ({
        ...(prev || {}),
        ...coreObj,
        saju: sajuData || prev?.saju,
      }));
    }
  }, [coreObj, profile]);

  const fetchProfileData = React.useCallback(async () => {
    let attempts = 0;
    const maxProfileRetries = 3;
    let success = false;

    while (attempts < maxProfileRetries && !success) {
      try {
        attempts++;
        let activeSession = initialSession;
        
        if (!activeSession) {
            activeSession = await ensureValidSession();
        }

        if (!activeSession) {
          if (attempts < maxProfileRetries) {
            await new Promise(r => setTimeout(r, 1000));
            continue;
          }
          setLoading(false);
          navigate('/');
          return;
        }

        const user = activeSession.user;
        const metadata = user.user_metadata || {};
        const { full_name, gender, mbti, birth_date, birth_time, analysis: metaAnalysis } = metadata;

        setProfile({
          id: user.id,
          name: full_name || user.email?.split('@')[0] || '사용자',
          email: user.email,
          gender: gender || '',
          mbti: mbti || '',
          birth_date: birth_date || '',
          birth_time: birth_date && birth_time ? birth_time : null
        });

        if (metaAnalysis) {
          setAnalysis(metaAnalysis);
        } else {
          try {
            const savedLocal = localStorage.getItem('mbti_saju_analysis');
            if (savedLocal) {
              setAnalysis(JSON.parse(savedLocal));
            }
          } catch (e) {
            console.error('Failed to restore local analysis backup:', e);
          }
        }
        
        if (refreshCredits) {
          refreshCredits(true, activeSession).catch(console.error);
        }

        success = true;
      } catch (err: any) {
        console.error(`Profile fetch attempt ${attempts} failed:`, err);
        if (attempts >= maxProfileRetries) {
          setError(err.message || '프로필 정보를 불러오는 중 오류가 발생했습니다.');
        } else {
          await new Promise(r => setTimeout(r, 1500));
        }
      } finally {
        if (success || attempts >= maxProfileRetries) {
          setLoading(false);
        }
      }
    }
  }, [navigate, initialSession, refreshCredits]);

  useEffect(() => {
    fetchProfileData();
  }, [fetchProfileData]);

  // Handle re-analysis manually
  const handleReAnalyze = () => {
    const isFirstTime = !analysis;
    if (isFirstTime) {
      handleGenerateAnalysis();
      return;
    }
    
    const cost = getCost('REGENERATE_MBTI_SAJU');
    const confirmMsg = `기존 분석 결과가 사라지고 새로 분석합니다.\n재분석은 ${cost}코인이 차감됩니다. 계속하시겠습니까?`;
    if (window.confirm(confirmMsg)) {
      handleGenerateAnalysis();
    }
  };

  const handleGenerateAnalysis = async () => {
    const isFirstTime = !analysis;
    const serviceType = isFirstTime ? 'MBTI_SAJU' : 'REGENERATE_MBTI_SAJU';
    
    // 크레딧 확인 (첫 분석은 무료, 재분석만 체크)
    if (!isFirstTime && !checkSufficientCredits(serviceType)) {
      setError(`크레딧이 부족합니다. (재분석 비용: ${getCost(serviceType)} 크레딧)`);
      setIsCreditModalOpen(true);
      return;
    }

    if (!profile) {
      setError('프로필 정보가 없습니다.');
      return;
    }

    if (!profile.birth_date || !profile.mbti) {
      setError('생년월일과 MBTI 정보가 필요합니다. [정보 수정] 버튼을 눌러 프로필을 먼저 등록해 주세요.');
      setIsEditModalOpen(true);
      return;
    }

    setAnalysisLoading(true);
    setError(null);

    try {
      const activeSession = await ensureValidSession() || initialSession;
      
      if (!activeSession) {
        throw new Error('인증되지 않은 사용자입니다. 다시 로그인해주세요.');
      }

      // 사주 만세력 데이터를 즉시 계산하여 스트리밍 시작과 동시에 화면에 표출
      const sajuData = calculateSaju(profile.birth_date, profile.birth_time ?? null);
      setAnalysis((prev: any) => ({
        ...(prev || {}),
        saju: sajuData,
        full_name: profile.name,
        gender: profile.gender,
        mbti: profile.mbti,
        birth_date: profile.birth_date,
      }));

      const requestPayload = {
        part: 'core',
        name: profile.name,
        gender: profile.gender,
        birthDate: profile.birth_date,
        birthTime: profile.birth_time,
        mbti: profile.mbti,
      };

      // Trigger the streaming hook
      submitCore(requestPayload);

    } catch (e: any) {
      console.error('handleGenerateAnalysis Error:', e);
      setError(e.message || '분석을 시작하는 중 오류가 발생했습니다.');
      setAnalysisLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);
    try {
      localStorage.clear();
      sessionStorage.clear();
      const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('Logout timeout')), 3000));
      await Promise.race([supabase.auth.signOut(), timeoutPromise]);
    } catch (error: any) {
      console.error('Logout error:', error);
    } finally {
      window.location.href = '/';
    }
  };

  const handleWithdraw = async () => {
    const confirm1 = window.confirm('정말 탈퇴하시겠습니까? 탈퇴 시 모든 크레딧 정보와 분석 리포트가 즉시 삭제됩니다.');
    if (!confirm1) return;
    
    const confirm2 = window.confirm('마지막 확인입니다. 모든 데이터는 복구가 불가능합니다. 정말 탈퇴하시겠습니까?');
    if (!confirm2) return;

    setLoading(true);
    try {
        const activeSession = await ensureValidSession() || initialSession;
        const activeUserId = activeSession?.user?.id;
        
        if (!activeUserId) throw new Error('유저 정보를 찾을 수 없습니다.');

        const { error: deleteError } = await supabase
          .from('profiles')
          .delete()
          .eq('id', activeUserId);

      if (deleteError) throw deleteError;

      alert('회원 탈퇴가 완료되었습니다. 그동안 이용해 주셔서 감사합니다.');
      await handleLogout();
    } catch (err: any) {
      console.error('Withdrawal error:', err);
      alert('탈퇴 처리 중 오류가 발생했습니다: ' + (err.message || '알 수 없는 오류'));
      setLoading(false);
    }
  };

  const handleNavigateToHistory = async () => {
    setIsNavigating(true);
    // Give time for spinner to be visible
    await new Promise(resolve => setTimeout(resolve, 800));
    navigate('/usage-history');
  };

  const renderAnalysisSection = (Icon: React.ElementType, title: string, content: string, isLongText = false) => (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow p-8">
      <div className="flex items-center gap-3 mb-6 border-b border-slate-100 pb-4">
        <Icon className="w-6 h-6 text-slate-600" />
        <h2 className="text-xl font-black text-slate-800">{title}</h2>
      </div>
      <div className={`text-slate-700 leading-8 font-normal whitespace-pre-line text-[15px] md:text-[16px] tracking-tight`}>
        {content}
      </div>
    </div>
  );


  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-slate-50 p-6">
        <Loader2 className="w-10 h-10 text-slate-950 animate-spin mb-4" />
        <span className="text-lg font-bold text-slate-700">마이페이지 정보를 불러오는 중입니다...</span>
      </div>
    );
  }

  if (error && !profile) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-slate-50 p-4">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-xl font-bold text-red-700 mb-2">오류가 발생했습니다</h2>
        <p className="text-red-500 text-center">{error}</p>
        <button onClick={() => { setError(null); navigate('/'); }} className="mt-6 btn-primary px-6 py-3">홈으로 돌아가기</button>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-slate-50 p-4">
        <AlertCircle className="w-12 h-12 text-orange-500 mb-4" />
        <h2 className="text-xl font-bold text-orange-700 mb-2">프로필을 찾을 수 없습니다</h2>
        <p className="text-orange-500 text-center">사용자 정보를 불러올 수 없습니다. 다시 로그인해주세요.</p>
        <button onClick={handleLogout} className="mt-6 btn-primary px-6 py-3">로그인 페이지로</button>
      </div>
    );
  }

  const formattedGender = profile.gender === 'male' ? '남성' : '여성';

  return (
    <div className="min-h-screen bg-slate-50/50 py-6 md:py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {error && (
          <div className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-xl flex items-center justify-between text-rose-700 text-sm font-bold animate-fade-in">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-rose-500 shrink-0" />
              <span>{error}</span>
            </div>
            <button onClick={() => setError(null)} className="text-rose-400 hover:text-rose-600 font-black px-2 py-1">✕</button>
          </div>
        )}

        <div className="bg-white shadow-xl rounded-2xl p-8 mb-10 border border-slate-100 relative">
          <div className='flex justify-between items-start mb-3'>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl md:text-4xl font-black text-slate-900">마이페이지</h1>
            </div>
            <div className='flex gap-2 min-w-fit'>
              <button onClick={() => setIsEditModalOpen(true)} className="btn-secondary px-3 py-2 text-xs md:text-sm bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg font-bold">정보 수정</button>
              <button onClick={handleLogout} className="btn-secondary px-3 py-2 text-xs md:text-sm bg-slate-900 text-white rounded-lg font-bold">로그아웃</button>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1 flex flex-col justify-between">
              <div>
                <p className="text-lg font-semibold text-slate-700 mb-4">{profile.name}님의 분석 리포트</p>
                <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-500 font-medium mb-6">
                  <span>MBTI: <span className="font-bold text-slate-950">{profile.mbti}</span></span>
                  <span>성별: <span className="font-bold text-slate-950">{formattedGender}</span></span>
                  <span>생년월일: <span className="font-bold text-slate-950">{profile.birth_date}</span></span>
                  <span>태어난 시간: <span className="font-bold text-slate-950">{profile.birth_time || '모름'}</span></span>
                </div>
              </div>

              {analysis?.fusionNickname && (
                <div className="mt-2 p-4 bg-gradient-to-r from-violet-600 to-indigo-600 rounded-2xl text-white shadow-md relative overflow-hidden group">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full blur-xl -mr-6 -mt-6 pointer-events-none group-hover:scale-125 transition-transform duration-500"></div>
                  <span className="text-[9px] font-black bg-white/20 px-2 py-0.5 rounded-md uppercase tracking-wider">융합 소울 시그니처</span>
                  <h3 className="text-lg font-black mt-1 flex items-center gap-1.5">
                    🔮 {analysis.fusionNickname}
                  </h3>
                  <p className="text-[11px] text-violet-100 mt-1 font-medium leading-relaxed">
                    {analysis.persona?.mbtiNickname ? `[${analysis.persona.mbtiNickname}] 성향과 사주 명리가 조합해 낸 특유의 기운과 아우라` : '당신의 성향과 운명이 조화를 이루어 뿜어내는 고유한 소울 컬러'}
                  </p>
                </div>
              )}
            </div>

            {/* Credit & Account Management Card */}
            <div className="flex-1 space-y-4">
              {/* Credit Balance Card */}
              <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Coins className="w-5 h-5 text-slate-950" />
                    <h3 className="font-bold text-slate-800">보유 크레딧</h3>
                  </div>
                  <button
                    onClick={() => setIsCreditModalOpen(true)}
                    className="text-slate-950 text-xs font-bold hover:underline"
                  >
                    충전하기
                  </button>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-black text-slate-950">{credits}</span>
                  <span className="text-sm text-slate-500">크레딧</span>
                </div>

                {/* Quick Transaction History Summary */}
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <div className="flex items-center justify-between text-xs text-slate-500 font-bold mb-2">
                    <span>최근 이용 내역</span>
                    <button onClick={() => navigate('/usage-history')} className="hover:text-slate-950 hover:underline">전체보기</button>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between p-2 bg-white/50 rounded-lg text-xs">
                      <span className="text-slate-600">결제 및 환불 문의</span>
                      <button onClick={() => navigate('/support')} className="text-slate-950 font-bold">고객센터</button>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 w-full mt-4">
                  <button
                    onClick={() => setIsCreditModalOpen(true)}
                    className="flex-1 py-2.5 bg-slate-950 text-white rounded-lg font-bold text-sm hover:bg-black transition-colors shadow-sm"
                  >
                    크레딧 충전하기
                  </button>
                  <button
                    onClick={handleNavigateToHistory}
                    className="flex-1 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-lg font-bold text-sm hover:bg-slate-50 transition-colors shadow-sm flex items-center justify-center gap-1.5"
                  >
                    <FileText className="w-4 h-4" /> 내역 보기
                  </button>
                </div>
              </div>


            </div>
          </div>
        </div>

        {analysisLoading ? (
          <div className="bg-white p-8 md:p-12 rounded-2xl shadow-xl border border-indigo-100 text-center animate-fade-in relative overflow-hidden my-6">
            {/* Ambient Background Glow */}
            <div className="absolute -top-24 -left-24 w-48 h-48 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none"></div>
            <div className="absolute -bottom-24 -right-24 w-48 h-48 bg-violet-500/10 rounded-full blur-3xl pointer-events-none"></div>

            <div className="relative z-10">
              {/* Spinner & Pulsing Sparkles Icon */}
              <div className="relative w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                <div className="absolute inset-0 border-4 border-indigo-100 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-t-indigo-600 border-r-violet-600 rounded-full animate-spin"></div>
                <Sparkles className="w-8 h-8 text-indigo-600 animate-pulse" />
              </div>

              <h2 className="text-2xl md:text-3xl font-black text-slate-900 mb-3">
                {profile?.name || '내담자'}님의 운명을 정밀 분석하고 있습니다
              </h2>
              <p className="text-slate-500 font-medium max-w-md mx-auto mb-8 text-sm md:text-base leading-relaxed">
                만세력 사주 원국과 MBTI 심리 유형을 교차 해독하여<br className="hidden sm:inline" />
                나만을 위한 '초밀착 융합 소울 리포트'를 도출 중입니다.
              </p>

              {/* Progress Steps Card */}
              <div className="max-w-md mx-auto bg-slate-50/80 rounded-2xl p-5 border border-slate-200/80 mb-6 text-left space-y-3 shadow-inner">
                <div className="flex items-center gap-3 text-xs md:text-sm font-bold text-indigo-900">
                  <div className="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs shrink-0 shadow-sm">1</div>
                  <span>한국천문연구원(KASI) 기준 정밀 만세력 사주 계산</span>
                </div>
                <div className="flex items-center gap-3 text-xs md:text-sm font-bold text-slate-800">
                  <div className="w-6 h-6 rounded-full bg-indigo-500 text-white flex items-center justify-center text-xs shrink-0 animate-pulse shadow-sm">2</div>
                  <span>MBTI 심리 기질 & 사주 오행 기운 교차 분석</span>
                </div>
                <div className="flex items-center gap-3 text-xs md:text-sm font-medium text-slate-500">
                  <div className="w-6 h-6 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-xs shrink-0">3</div>
                  <span>타고난 빛과 그림자 & 맞춤형 라이프 솔루션 작성</span>
                </div>
              </div>

              {coreObj?.reportTitle && (
                <div className="mb-4 inline-flex items-center gap-2 bg-indigo-50 text-indigo-700 px-4 py-2 rounded-full text-xs md:text-sm font-bold animate-pulse">
                  <span>🔮 {coreObj.reportTitle}</span>
                </div>
              )}

              <div className="flex items-center justify-center gap-2 text-xs text-slate-400 font-medium">
                <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
                <span>약 10~15초 소요됩니다. 잠시만 기다려주세요.</span>
              </div>
            </div>
          </div>
        ) : analysis ? (
          <div className="space-y-6 animate-fade-up">
            {/* Saju Type Section */}
            {(analysis.nature || (analysis as any).typeDescription) && (
              <div className="bg-slate-50 border border-slate-200 rounded-2xl shadow-lg shadow-slate-200/50 p-8">
                <div className="flex items-center gap-3 mb-8">
                  <Sparkles className="w-6 h-6 text-slate-950" />
                  <h2 className="text-2xl font-bold text-slate-900">운명 분석 리포트</h2>
                </div>

                {/* Manseok Saju Grid */}
                {analysis.saju && <SajuGrid saju={analysis.saju} />}

                <div className="space-y-6">
                  {/* Day Master Analysis */}
                  <div className='bg-white bg-opacity-60 rounded-xl p-5 border border-slate-100'>
                    <h3 className="font-bold text-slate-600 mb-2 flex items-center gap-1.5">
                      <Gem className="w-4 h-4 text-violet-500" />
                      나의 천간 및 본성
                    </h3>
                    <p className="text-slate-950 text-lg font-black mb-1">
                      {analysis.nature?.dayMasterAnalysis || (analysis as any).typeDescription}
                    </p>
                    {/* Show calculation details if available */}
                    {analysis.saju && (
                      <div className="mt-4 pt-4 border-t border-slate-200/50">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="inline-block bg-slate-950 text-white px-2 py-1 rounded text-xs font-bold">
                            {analysis.saju.dayMaster.korean}({analysis.saju.dayMaster.chinese})
                          </span>
                          <span className="text-xs font-bold text-slate-500">일간의 특징</span>
                        </div>
                        <p className="text-sm text-slate-700 leading-relaxed font-medium">
                          {analysis.saju.dayMaster.description}
                        </p>
                        {analysis.saju.pillars && (
                          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                             <div className="bg-gradient-to-br from-white to-slate-50/30 p-2.5 rounded-xl text-center border border-slate-200/50 shadow-sm">
                                <span className="block text-[9px] text-slate-400 font-black uppercase tracking-tight mb-1">12운성</span>
                                <span className="text-sm font-black text-slate-900">{analysis.saju.pillars.day?.twelveStages || '-'}</span>
                             </div>
                             <div className="bg-gradient-to-br from-white to-slate-50/30 p-2.5 rounded-xl text-center border border-slate-200/50 shadow-sm">
                                <span className="block text-[9px] text-slate-400 font-black uppercase tracking-tight mb-1">12신살</span>
                                <span className="text-sm font-black text-slate-900">{analysis.saju.pillars.day?.twelveSpirits || '-'}</span>
                             </div>
                             <div className="bg-gradient-to-br from-white to-amber-50/30 p-2.5 rounded-xl text-center border border-amber-100/50 shadow-sm">
                                <span className="block text-[9px] text-amber-500 font-black uppercase tracking-tight mb-1">지지십성</span>
                                <span className={`text-sm font-black ${getShiShenStyle(translateShiShen(analysis.saju.pillars.day?.zhiShiShen)).split(' ')[0]}`}>{translateShiShen(analysis.saju.pillars.day?.zhiShiShen) || '-'}</span>
                             </div>
                             <div className="bg-gradient-to-br from-white to-slate-50/50 p-2.5 rounded-xl text-center border border-slate-100/50 shadow-sm">
                                <span className="block text-[9px] text-slate-400 font-black uppercase tracking-tight mb-1">지장간</span>
                                <span className="text-sm font-bold text-slate-700">{formatHiddenStems(analysis.saju.pillars.day?.hiddenStems)}</span>
                             </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Five Elements Analysis */}
                  {(analysis.fiveElements || (analysis as any).elementAnalysis) && (
                    <div>
                      <h3 className="font-bold text-slate-900 mb-2">🌊 오행 분석</h3>
                      <p className="text-slate-700 leading-relaxed">
                        {analysis.fiveElements?.summary || (analysis as any).elementAnalysis}
                      </p>
                    </div>
                  )}

                  {/* Five Elements Visualizer */}
                  {analysis.saju && analysis.saju.elements && (
                    <div className="mt-4">
                      <h4 className="text-sm font-bold text-slate-500 mb-3 uppercase tracking-wider">오행 분포 (Five Elements)</h4>
                      <div className="grid grid-cols-3 md:grid-cols-5 gap-2 text-center">
                        {[
                          { label: '목(木)', count: analysis.saju.elements.wood, color: 'bg-green-100 text-green-700 border-green-200' },
                          { label: '화(火)', count: analysis.saju.elements.fire, color: 'bg-red-100 text-red-700 border-red-200' },
                          { label: '토(土)', count: analysis.saju.elements.earth, color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
                          { label: '금(金)', count: analysis.saju.elements.metal, color: 'bg-slate-100 text-slate-700 border-slate-200' },
                          { label: '수(水)', count: analysis.saju.elements.water, color: 'bg-blue-100 text-blue-700 border-blue-200' }
                        ].map((el) => (
                          <div key={el.label} className={`rounded-xl p-2 border ${el.color} flex flex-col items-center justify-center`}>
                            <span className="text-2xl font-black mb-1">{el.count}</span>
                            <span className="text-xs font-bold">{el.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

                    {renderAnalysisSection(Key, "MBTI와 사주 핵심 키워드", 
                      Array.isArray(analysis.keywords) 
                        ? (analysis.keywords as string[]).join(', ') 
                        : (typeof analysis.keywords === 'string' ? analysis.keywords : '')
                    )}

            {/* 1. MBTI × 사주 심층 융합 진단 */}
            {analysis.deepIntegration && (analysis.deepIntegration.sajuBaseAnalysis || analysis.deepIntegration.mbtiIntegration) && (
              <div className="bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow p-8 space-y-6">
                <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                  <Sparkles className="w-6 h-6 text-violet-600" />
                  <h2 className="text-xl font-black text-slate-800">MBTI × 사주 심층 융합 분석</h2>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* 사주 명리적 해석 */}
                  <div className="bg-slate-50/50 p-6 rounded-xl border border-slate-100 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="inline-block bg-violet-600 text-white px-2 py-0.5 rounded text-[10px] font-bold">1단계</span>
                        <h4 className="text-sm font-bold text-slate-850">타고난 운명적 본질 (사주 풀이)</h4>
                      </div>
                      <p className="text-slate-650 leading-relaxed text-sm whitespace-pre-line font-medium">
                        {analysis.deepIntegration.sajuBaseAnalysis}
                      </p>
                    </div>
                  </div>

                  {/* MBTI 심리 결합 */}
                  <div className="bg-slate-50/50 p-6 rounded-xl border border-slate-100 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="inline-block bg-indigo-600 text-white px-2 py-0.5 rounded text-[10px] font-bold">2단계</span>
                        <h4 className="text-sm font-bold text-slate-850">현실적 성격의 역동 (사주 × MBTI 융합)</h4>
                      </div>
                      <p className="text-slate-650 leading-relaxed text-sm whitespace-pre-line font-medium">
                        {analysis.deepIntegration.mbtiIntegration}
                      </p>
                    </div>
                  </div>
                </div>

                {/* 시너지 포인트 */}
                {analysis.deepIntegration.synergyPoints && analysis.deepIntegration.synergyPoints.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-slate-100">
                    <h4 className="text-xs font-bold text-slate-400 mb-4 uppercase tracking-wider">핵심 융합 시너지 포인트</h4>
                    <div className="grid md:grid-cols-3 gap-4">
                      {analysis.deepIntegration.synergyPoints.map((point: any, idx: number) => (
                        <div key={idx} className="bg-white border border-slate-150 p-5 rounded-xl shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group">
                          <div className="absolute top-0 left-0 w-1 h-full bg-violet-500"></div>
                          <h5 className="font-bold text-slate-800 mb-2 flex items-center gap-1.5 text-xs">
                            <span className="text-violet-500 font-extrabold text-xs">#{idx + 1}</span>
                            {point.subtitle}
                          </h5>
                          <p className="text-xs text-slate-600 leading-relaxed font-medium">
                            {point.content}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 구 버전 호환용 융합 분석 렌더링 */}
            {(!analysis.deepIntegration || (!analysis.deepIntegration.sajuBaseAnalysis && !analysis.deepIntegration.mbtiIntegration)) && 
             (analysis.deepIntegration?.integrationPoints || (analysis as any).commonalities) && (
              renderAnalysisSection(Users, "두 결과의 공통점 및 융합 분석",
                analysis.deepIntegration?.integrationPoints 
                  ? analysis.deepIntegration.integrationPoints.map((p: any) => p.content).join('\n\n') 
                  : (analysis as any).commonalities, 
                true)
            )}

            {/* 2. 나만의 인생 지침서 (Life Guideline) */}
            {analysis.lifeGuideline && (
              <div className="bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow p-8 space-y-6">
                <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                  <Award className="w-6 h-6 text-amber-500" />
                  <h2 className="text-xl font-black text-slate-800">성격의 빛과 그림자 (무의식 분석)</h2>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* 빛 (강점) */}
                  <div className="p-6 rounded-2xl bg-gradient-to-br from-amber-50/50 to-orange-50/20 border border-amber-100/60 relative overflow-hidden">
                    <div className="absolute -right-4 -bottom-4 opacity-10 pointer-events-none">
                      <Sun className="w-24 h-24 text-amber-500" />
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="bg-amber-100 text-amber-800 p-1.5 rounded-lg">
                        <Sun className="w-4 h-4" />
                      </span>
                      <h4 className="text-sm font-bold text-slate-850">내가 가진 성향적 빛 (강점)</h4>
                    </div>
                    <p className="text-slate-650 leading-relaxed text-sm whitespace-pre-line font-medium relative z-10">
                      {analysis.lifeGuideline.lightAndShadow?.light}
                    </p>
                  </div>

                  {/* 그림자 (약점 & 팩폭) */}
                  <div className="p-6 rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200/60 relative overflow-hidden">
                    <div className="absolute -right-4 -bottom-4 opacity-10 pointer-events-none">
                      <Moon className="w-24 h-24 text-slate-500" />
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="bg-slate-200 text-slate-700 p-1.5 rounded-lg">
                        <Moon className="w-4 h-4" />
                      </span>
                      <h4 className="text-sm font-bold text-slate-850">무의식적인 어둠의 그림자 (팩폭)</h4>
                    </div>
                    <p className="text-slate-650 leading-relaxed text-sm whitespace-pre-line font-medium relative z-10">
                      {analysis.lifeGuideline.lightAndShadow?.shadow}
                    </p>
                  </div>
                </div>

                {/* 그림자 해결 솔루션 */}
                <div className="p-5 bg-emerald-50/30 rounded-xl border border-emerald-100/50 flex gap-4 items-start">
                  <span className="bg-emerald-100 text-emerald-800 p-2 rounded-lg shrink-0 mt-0.5">
                    <Zap className="w-4 h-4" />
                  </span>
                  <div>
                    <h5 className="font-bold text-slate-800 text-xs mb-1">그림자를 극복하기 위한 라이프 솔루션</h5>
                    <p className="text-xs text-slate-650 leading-relaxed font-medium">
                      {analysis.lifeGuideline.lightAndShadow?.solution}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* 3. 나를 깨우는 행운의 부스터 (Lucky Booster) */}
            {analysis.lifeGuideline && analysis.lifeGuideline.luckyBooster && (
              <div className="bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow p-8 space-y-6">
                <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                  <Gem className="w-6 h-6 text-rose-500" />
                  <h2 className="text-xl font-black text-slate-800">기운을 돋우는 나만의 럭키 부스터</h2>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* 행운의 색 */}
                  <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100 text-center hover:bg-slate-100/50 transition-colors">
                    <span className="mx-auto mb-3 bg-rose-50 text-rose-500 p-3 rounded-full flex items-center justify-center w-12 h-12">
                      <Palette className="w-6 h-6" />
                    </span>
                    <h4 className="font-bold text-slate-700 text-xs mb-1">행운의 색상</h4>
                    <p className="text-xs text-slate-500 leading-relaxed font-medium font-bold">
                      {analysis.lifeGuideline.luckyBooster?.luckyColor}
                    </p>
                  </div>

                  {/* 행운의 아이템 */}
                  <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100 text-center hover:bg-slate-100/50 transition-colors">
                    <span className="mx-auto mb-3 bg-amber-50 text-amber-600 p-3 rounded-full flex items-center justify-center w-12 h-12">
                      <Gift className="w-6 h-6" />
                    </span>
                    <h4 className="font-bold text-slate-700 text-xs mb-1">행운의 아이템</h4>
                    <p className="text-xs text-slate-500 leading-relaxed font-medium font-bold">
                      {analysis.lifeGuideline.luckyBooster?.luckyItem}
                    </p>
                  </div>

                  {/* 행운의 장소 */}
                  <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100 text-center hover:bg-slate-100/50 transition-colors">
                    <span className="mx-auto mb-3 bg-indigo-50 text-indigo-500 p-3 rounded-full flex items-center justify-center w-12 h-12">
                      <MapPin className="w-6 h-6" />
                    </span>
                    <h4 className="font-bold text-slate-700 text-xs mb-1">행운의 장소</h4>
                    <p className="text-xs text-slate-500 leading-relaxed font-medium font-bold">
                      {analysis.lifeGuideline.luckyBooster?.luckyPlace}
                    </p>
                  </div>

                  {/* 데일리 루틴 */}
                  <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100 text-center hover:bg-slate-100/50 transition-colors">
                    <span className="mx-auto mb-3 bg-emerald-50 text-emerald-600 p-3 rounded-full flex items-center justify-center w-12 h-12">
                      <Coffee className="w-6 h-6" />
                    </span>
                    <h4 className="font-bold text-slate-700 text-xs mb-1">추천 데일리 루틴</h4>
                    <p className="text-xs text-slate-500 leading-relaxed font-medium font-bold">
                      {analysis.lifeGuideline.luckyBooster?.dailyRoutine}
                    </p>
                  </div>
                </div>
              </div>
            )}


            {/* Navigation Buttons for Deep Analysis */}
            <div className="grid md:grid-cols-2 gap-4 mt-8">
              <button
                onClick={() => onOpenDeepReport()}
                className="relative overflow-hidden bg-slate-950 text-white p-6 rounded-2xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all text-left flex flex-col justify-between h-40 group border border-slate-800"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-violet-600/20 rounded-full blur-3xl -mr-10 -mt-10 pointer-events-none"></div>
                <div className="flex justify-between items-start z-10">
                  <span className="text-slate-400 mb-2"><FileText className="w-6 h-6 text-violet-400" /></span>
                  <span className="bg-violet-600 text-white text-[10px] font-black px-2 py-1 rounded tracking-tight">수기 작성 프리미엄</span>
                </div>
                <div className="z-10">
                  <h3 className="text-xl font-bold mb-1">결합 심층 리포트 받기</h3>
                  <p className="text-slate-400 text-sm opacity-100 transition-opacity flex justify-between items-center">
                    전문가 직접 분석 <span className="opacity-0 group-hover:opacity-100 transition-opacity">&rarr;</span>
                  </p>
                </div>
              </button>

              <div className="grid gap-4">
                <button
                  onClick={onJamidusuClick}
                  className="bg-white border border-slate-200 text-slate-800 p-5 rounded-2xl shadow-md hover:shadow-lg hover:-translate-y-1 transition-all text-left flex items-center gap-4 group"
                >
                  <span className="bg-indigo-50 p-3 rounded-full flex items-center justify-center shrink-0">
                    <Star className="w-6 h-6 text-indigo-500" />
                  </span>
                  <div>
                    <h3 className="font-bold text-lg">자미두수</h3>
                    <p className="text-slate-500 text-sm">자미두수 메인 캐릭터</p>
                  </div>
                </button>

                <button
                  onClick={onOpenCompatibility}
                  className="bg-white border border-slate-200 text-slate-800 p-5 rounded-2xl shadow-md hover:shadow-lg hover:-translate-y-1 transition-all text-left flex items-center gap-4 group"
                >
                  <span className="bg-rose-50 p-3 rounded-full flex items-center justify-center shrink-0">
                    <Users className="w-6 h-6 text-rose-500" />
                  </span>
                  <div>
                    <h3 className="font-bold text-lg">궁합 보러 가기</h3>
                    <p className="text-slate-500 text-sm">그 사람과의 시너지는?</p>
                  </div>
                </button>
              </div>
            </div>

            <div className='text-center mt-12'>
              <button
                onClick={handleReAnalyze}
                disabled={analysisLoading}
                className="text-slate-400 hover:text-slate-950 underline text-sm transition-colors"
              >
                {analysisLoading ? '분석 중...' : '다시 분석하기'}
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center bg-white p-12 rounded-2xl shadow-lg border border-slate-100">
            <Sparkles className={`w-12 h-12 mx-auto mb-4 ${analysisLoading ? 'animate-pulse text-indigo-600' : 'text-slate-950'}`} />
            <h2 className="text-2xl font-bold text-slate-800 mb-3">
              {analysisLoading ? "운명을 분석하고 있습니다..." : "아직 분석 결과가 없습니다"}
            </h2>
            <p className="text-slate-500 font-medium mb-8">
              {analysisLoading ? "사주 원국과 MBTI 기질을 융합 해독 중입니다. 잠시만 기다려주세요." : "아래 버튼을 눌러 종합 분석을 받아보세요."}
            </p>
            <button
              onClick={handleGenerateAnalysis}
              className="btn-primary w-full sm:w-auto px-10 py-4 text-lg font-bold flex items-center justify-center gap-2 mx-auto"
              disabled={analysisLoading}
            >
              {analysisLoading ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <span>분석 중입니다...</span>
                </>
              ) : (
                analysis 
                  ? `내 운명 재분석 하기 (${getCost('REGENERATE_MBTI_SAJU')} 크레딧)`
                  : "내 운명 분석 시작 (무료)"
              )}
            </button>
          </div>
        )}

        {/* Footer Support Links */}
        <div className="mt-16 border-t border-slate-200 pt-8 text-center text-slate-400 text-sm">
          <div className="flex justify-center gap-6 mb-4">
            <button onClick={() => navigate('/terms')} className="hover:text-slate-600">이용약관</button>
            <button onClick={() => navigate('/privacy')} className="hover:text-slate-600">개인정보처리방침</button>
            <button onClick={() => navigate('/support')} className="hover:text-slate-600">고객센터 문의</button>
          </div>
          <div className='mb-4'>
            <button onClick={handleWithdraw} className="text-slate-300 hover:text-rose-400 underline transition-colors text-xs">회원 탈퇴</button>
          </div>
          <p>© 2026 MBTI Saju. All rights reserved.</p>
        </div>
 
        {/* Navigation Overlay Spinner */}
        {isNavigating && (
          <div className="fixed inset-0 bg-white/60 backdrop-blur-[2px] z-[9999] flex flex-col items-center justify-center animate-fade-in">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-indigo-100 rounded-full"></div>
              <div className="absolute top-0 left-0 w-16 h-16 border-4 border-t-indigo-600 rounded-full animate-spin"></div>
            </div>
            <p className="mt-4 text-indigo-900 font-bold animate-pulse">정보를 불러오는 중입니다...</p>
          </div>
        )}
      </div>

      <AnalysisModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        mode="edit"
        initialData={profile}
        onUpdate={() => fetchProfileData()}
      />

      <CreditPurchaseModal
        isOpen={isCreditModalOpen}
        onClose={() => setIsCreditModalOpen(false)}
        userEmail={profile?.email}
        currentCredits={credits}
        onSuccess={async (planId: string, pricePaid: number, creditAmount: number, paymentId?: string) => {
          await purchaseCredits(planId, pricePaid, creditAmount, paymentId);
        }}
      />
    </div>
  );
};

export default MyPage;
